import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import useExcel from "../hooks/useExcel";

interface EANSearchProps {
  onAddProduct: (ean: string) => void;
  loading: boolean;
  setShowImportExcelDialog: (show: boolean) => void;
  setShowCreateMaterialView: (show: boolean) => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2rem;
`;

const rollingShadow = keyframes`
  0% {
    background-position: -50% 0;
  }
  100% {
    background-position: 150% 0;
  }
`;

const StyledInputText = styled(InputText)`
  margin-right: 10px;
  min-width: 250px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  position: relative;
  padding-bottom: 6px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 10px;
    bottom: 0;
    height: 6px;
    background: radial-gradient(
      ellipse at center,
      var(--sumi-blue) 0%,
      transparent 70%
    );
    background-size: 50% 100%;
    background-repeat: no-repeat;
    opacity: 0;
  }

  &.loading::after {
    opacity: 1;
    animation: ${rollingShadow} 1.2s linear infinite;
  }
`;

const DownloadLink = styled.span`
  color: var(--sumi-blue);
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 5px;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid var(--sumi-blue);
    outline-offset: 2px;
  }
`;

const EANSearch: React.FC<EANSearchProps> = ({
  onAddProduct,
  setShowImportExcelDialog,
  loading,
  setShowCreateMaterialView,
}) => {
  const { t } = useTranslation();
  const [ean, setEan] = useState("");

  const { downloadExcelTemplate } = useExcel();

  const handleAddProduct = () => {
    onAddProduct(ean);
    setEan("");
  };

  const handleDownloadExcelTemplate = async () => {
    await downloadExcelTemplate();
  };

  return (
    <Container>
      <InputContainer className={classNames({ loading })}>
        <StyledInputText
          value={ean}
          onChange={(e) => {
            setEan(e.target.value.toUpperCase());
          }}
          placeholder={t("label-ean-or-gtin")}
          invalid={ean.length > 14}
          disabled={loading}
        />
      </InputContainer>
      <div style={{ display: "flex", margin: "40px 0", gap: "1rem" }}>
        <Button
          id="add-product-button"
          type="button"
          label={t("cmd-add-product")}
          onClick={handleAddProduct}
          disabled={ean.length < 1 || loading}
          style={{ marginBottom: "auto" }}
          loading={loading}
        />
        <Button
          type="button"
          label={t("cmd-create-material")}
          onClick={() => setShowCreateMaterialView(true)}
          outlined
          style={{ marginBottom: "auto" }}
        />
        <Button
          type="button"
          label={t("cmd-load-from-file")}
          onClick={() => setShowImportExcelDialog(true)}
          style={{ marginBottom: "auto" }}
          outlined
          disabled={loading}
        />
        <DownloadLink
          role="button"
          tabIndex={0}
          onClick={handleDownloadExcelTemplate}
          onKeyDown={(e) => e.key === "Enter" && handleDownloadExcelTemplate()}
        >
          {t("cmd-download-excel-template")}
        </DownloadLink>
      </div>
    </Container>
  );
};

export default EANSearch;
